/**
 * Text-to-speech for @neuro/ui-chat's voice mode (the assistant overlay),
 * backed by core's authenticated `POST /api/speech/synthesize`.
 *
 * The browser never calls the speech-service directly: it has no CORS, and
 * production nginx doesn't route to it (the old relative `/speech` URL only
 * worked through the Vite dev proxy).
 *
 * Neuro speaks with the voice the listener picked for `neuro-ai` in chat
 * (GET /api/speech/preferences), or their default voice, or the same "Auto"
 * voice chat derives from the sender id.
 */

import type { TTSProvider, TTSSpeakOptions } from '@neuro/ui-chat';

const MAX_CHARS = 900;
const NEURO_SENDER_ID = 'neuro-ai';
/** A 44-byte, zero-length WAV: playing it inside a gesture unlocks the element. */
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/** Keep in sync with AUTO_VOICE_POOL in plugins/chat/ui/lib/speech-voices.ts. */
const AUTO_VOICE_POOL = [
  'af_alloy', 'af_aoede', 'af_bella', 'af_heart', 'af_jadzia', 'af_jessica', 'af_kore', 'af_nicole', 'af_nova',
  'af_river', 'af_sarah', 'af_sky', 'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael',
  'am_onyx', 'am_puck', 'bf_alice', 'bf_emma', 'bf_lily', 'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis',
];

/** Same 32-bit FNV-1a pick as chat's autoVoiceFor, so Neuro sounds the same in both places. */
export function autoVoiceFor(senderId: string, available: ReadonlySet<string> | null): string {
  let hash = 0x811c9dc5;
  const key = senderId.trim().toLowerCase();
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const offered = available ? AUTO_VOICE_POOL.filter((id) => available.has(id)) : AUTO_VOICE_POOL;
  const pool = offered.length > 0 ? offered : available && available.size > 0 ? [...available].sort() : AUTO_VOICE_POOL;
  return pool[(hash >>> 0) % pool.length];
}

/** Splits text at sentence ends into pieces the synthesize route accepts. */
export function splitForSynthesis(text: string, max = MAX_CHARS): string[] {
  // Sentence ends are . ! ? followed by whitespace (so "example.com" stays whole), or a line break.
  const sentences = text.split(/(?<=[.!?])\s+|\n+/).map((s) => s.trim()).filter(Boolean);
  const pieces: string[] = [];
  let current = '';
  for (let sentence of sentences) {
    while (sentence.length > max) {
      if (current) {
        pieces.push(current);
        current = '';
      }
      const cut = sentence.lastIndexOf(' ', max);
      const at = cut > max / 3 ? cut : max;
      pieces.push(sentence.slice(0, at).trim());
      sentence = sentence.slice(at).trim();
    }
    if (!sentence) continue;
    if (current && current.length + 1 + sentence.length > max) {
      pieces.push(current);
      current = sentence;
    } else {
      current = current ? `${current} ${sentence}` : sentence;
    }
  }
  if (current) pieces.push(current);
  return pieces;
}

interface Preferences {
  defaultVoiceId?: string | null;
  speed?: number;
  voiceMap?: Record<string, string>;
}

const unwrap = <T,>(payload: unknown): T =>
  (payload && typeof payload === 'object' && 'data' in payload ? (payload as { data: T }).data : payload) as T;

export function createSpeechServiceTTSProvider(options: {
  getToken: () => string | null;
  /** For tests. */
  fetchImpl?: typeof fetch;
  createAudio?: () => HTMLAudioElement;
}): TTSProvider {
  const fetchImpl = options.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  let audio: HTMLAudioElement | null = null;
  let controller: AbortController | null = null;
  let speaking = false;
  let voicePromise: Promise<{ voiceId?: string; speed?: number }> | null = null;

  const headers = (json: boolean): Record<string, string> => {
    const result: Record<string, string> = json ? { 'Content-Type': 'application/json' } : {};
    const token = options.getToken();
    if (token) result.Authorization = `Bearer ${token}`;
    return result;
  };

  const element = (): HTMLAudioElement => {
    if (!audio) {
      audio = options.createAudio ? options.createAudio() : new Audio();
      audio.preload = 'auto';
    }
    return audio;
  };

  // Browsers only allow audio after a gesture: the first tap (e.g. on the speaker toggle) unlocks the element.
  if (typeof document !== 'undefined') {
    const unlock = () => {
      if (speaking) return;
      const el = element();
      el.src = SILENT_WAV;
      void el.play()?.catch(() => {});
    };
    document.addEventListener('pointerdown', unlock, { capture: true, once: true });
  }

  /** The listener's voice for Neuro; looked up once per provider. */
  const neuroVoice = () => {
    voicePromise ??= (async () => {
      try {
        const [prefsRes, voicesRes] = await Promise.all([
          fetchImpl('/api/speech/preferences', { headers: headers(false), credentials: 'include' }),
          fetchImpl('/api/speech/voices', { headers: headers(false), credentials: 'include' }),
        ]);
        const prefs = prefsRes.ok ? unwrap<Preferences>(await prefsRes.json()) : {};
        const voices = voicesRes.ok ? unwrap<Array<{ id: string }>>(await voicesRes.json()) : [];
        const available = Array.isArray(voices) && voices.length > 0 ? new Set(voices.map((v) => v.id)) : null;
        const usable = (id: string | null | undefined): id is string => !!id && (!available || available.has(id));
        const mapped = prefs?.voiceMap?.[NEURO_SENDER_ID];
        const voiceId = usable(mapped) ? mapped : usable(prefs?.defaultVoiceId) ? prefs.defaultVoiceId : autoVoiceFor(NEURO_SENDER_ID, available);
        return { voiceId, speed: typeof prefs?.speed === 'number' ? prefs.speed : undefined };
      } catch {
        return {};
      }
    })();
    return voicePromise;
  };

  const play = (blob: Blob, signal: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      const el = element();
      const url = URL.createObjectURL(blob);
      const done = (err?: unknown) => {
        el.onended = null;
        el.onerror = null;
        signal.removeEventListener('abort', onAbort);
        URL.revokeObjectURL(url);
        if (err) reject(err);
        else resolve();
      };
      const onAbort = () => {
        el.pause();
        done();
      };
      el.onended = () => done();
      el.onerror = () => done(new Error('The browser could not play the audio'));
      signal.addEventListener('abort', onAbort, { once: true });
      el.src = url;
      el.play()?.catch((err: unknown) => {
        if (!signal.aborted) done(err);
      });
    });

  const cancel = () => {
    controller?.abort();
    controller = null;
    speaking = false;
  };

  return {
    isSupported: () => typeof Audio !== 'undefined' && typeof fetch !== 'undefined',

    async speak(text: string, speakOptions?: TTSSpeakOptions): Promise<void> {
      cancel();
      const pieces = splitForSynthesis(text);
      if (pieces.length === 0) return;
      const ctrl = new AbortController();
      controller = ctrl;
      speaking = true;
      try {
        const { voiceId, speed } = await neuroVoice();
        for (const piece of pieces) {
          if (ctrl.signal.aborted) return;
          const res = await fetchImpl('/api/speech/synthesize', {
            method: 'POST',
            headers: headers(true),
            credentials: 'include',
            signal: ctrl.signal,
            body: JSON.stringify({ text: piece, voiceId: voiceId ?? speakOptions?.voice, speed: speed ?? speakOptions?.rate }),
          });
          if (!res.ok) throw new Error(`Speech request failed (${res.status})`);
          const blob = await res.blob();
          if (ctrl.signal.aborted) return;
          await play(blob, ctrl.signal);
        }
      } catch (err) {
        if (ctrl.signal.aborted || (err as { name?: string })?.name === 'AbortError') return;
        throw err;
      } finally {
        if (controller === ctrl) {
          controller = null;
          speaking = false;
        }
      }
    },

    cancel,
    isSpeaking: () => speaking,
  };
}
